import { Box } from "@gemini/core";
import { ConstructionSiteHelmetIcon } from "@gemini/icons";
import type { ReactNode } from "react";

import { useTranslate } from "../../../utils/useTranslate";

import { ErrorPageBackground } from "./artworks/ErrorPageBackground";

interface Props {
  buttons: ReactNode;
  titleTransKey: `${string}.${"title"}`;
  infoTransKey: `${string}.${"info"}`;
}

/**
 * Base “page” component used to compose error pages.
 */
export const BaseErrorPage = ({
  buttons,
  titleTransKey,
  infoTransKey,
}: Props) => {
  const t = useTranslate();

  return (
    <Box
      as="main"
      flex="1 0 auto"
      display="grid"
      gridTemplateRows="1fr auto"
      placeItems="center"
      overflowX="hidden" // hide SVG when offscreen on narrow viewports
    >
      <Box
        paddingTop="spacing.56"
        paddingHorizontal="spacing.16"
        paddingBottom={{
          "breakpoint.lg": "spacing.80",
          "breakpoint.base": "spacing.24",
        }}
        unsafe_style={{
          maxWidth: "33.75rem", // 540px
        }}
        display="inline-flex"
        flexDirection="column"
        alignItems="center"
        gap="spacing.12"
      >
        <ConstructionSiteHelmetIcon
          marginBottom={{
            "breakpoint.lg": "spacing.16",
            "breakpoint.base": "spacing.8",
          }}
          size="sizing.24"
          color="color.border.status.error.default"
        />
        <Box
          as="h1"
          marginVertical="spacing.none"
          typography="typography.headline.22.bold"
          textAlign="center"
          role="alert"
          aria-live="assertive"
        >
          {t(titleTransKey)}
        </Box>
        <Box
          as="p"
          marginTop="spacing.4"
          textAlign="center"
          typography="typography.body.16.regular"
        >
          {t(infoTransKey)}
        </Box>
        {buttons}
      </Box>
      <ErrorPageBackground />
    </Box>
  );
};
